import { useMemo, useRef } from 'react';

import { Button, Divider, ExcelIcon, Select, type TypeSelectOption } from '@thefarmersfront/kitchen';

import type { IRowNode } from 'ag-grid-community';

import { Table, TableBottomField } from '~/components/common';
import { TableTopField } from '~/components/common/TableTopField';

import { getDeliveryAgencyOrdersColumnDefs } from '~/utils/delivery-agency/table';

import {
  DELIVERY_AGENCY_ORDER_SORT_OPTIONS,
  DELIVERY_AGENCY_PAGE_SIZE,
  type TypeDeliveryAgencySortBy,
} from '~/constants/delivery-agency';

import type { TypePagination } from '~/types/common.types';
import type { TypeDeliveryAgencyOrder } from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyOrderTable.module.scss';

const DeliveryAgencyOrderTable = ({
  data,
  pagination,
  sortBy,
  selectedItems,
  isDownloadReady,
  onChangePageLimit,
  onChangeSort,
  onChangePage,
  onChangeRowSelection,
  onClickOrderRequestCancel,
  onClickViewDetail,
  onClickOrderExcelDownload,
  onClickOrderExcelUpload,
  onClickExcelTemplateDownload,
}: {
  data: TypeDeliveryAgencyOrder[] | null;
  pagination: TypePagination;
  sortBy: TypeDeliveryAgencySortBy;
  selectedItems: TypeDeliveryAgencyOrder[];
  isDownloadReady: boolean;
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangeSort: (value: TypeDeliveryAgencySortBy) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: IRowNode[]) => void;
  onClickOrderRequestCancel: () => void;
  onClickViewDetail: (order: TypeDeliveryAgencyOrder) => void;
  onClickOrderExcelDownload: () => void;
  onClickOrderExcelUpload: () => void;
  onClickExcelTemplateDownload: () => void;
}) => {
  const onClickViewDetailRef = useRef(onClickViewDetail);
  onClickViewDetailRef.current = onClickViewDetail;

  const { columnDefs } = useMemo(
    () =>
      getDeliveryAgencyOrdersColumnDefs({
        currentPage: pagination.currentPage - 1,
        pageSize: pagination.pageSize,
        onClickViewDetail: (order) => onClickViewDetailRef.current(order),
      }),
    [pagination.currentPage, pagination.pageSize]
  );

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              status="error"
              disabled={selectedItems.length === 0}
              onClick={onClickOrderRequestCancel}
            >
              주문 취소
            </Button>
            <Divider className={styles['divider']} />
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickOrderExcelDownload}
            >
              주문 정보 전체 다운로드
            </Button>
            <Divider className={styles['divider']} />
            <Button size="tiny" variant="outlined" color="tertiary" onClick={onClickExcelTemplateDownload}>
              주문 엑셀 양식 다운로드
            </Button>
            <Button size="tiny" variant="outlined" color="tertiary" onClick={onClickOrderExcelUpload}>
              주문 엑셀 등록
            </Button>
          </div>
        }
      />
      <div className={styles['table-wrapper']}>
        <Table
          data={data}
          options={{ columnDefs }}
          rowStatusType={data === null ? 'INIT' : 'EMPTY'}
          onSelectionChange={onChangeRowSelection}
        />
      </div>
      <TableBottomField
        pageSizeOptions={Object.values(DELIVERY_AGENCY_PAGE_SIZE)}
        currentPage={pagination.currentPage - 1}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={(page) => onChangePage(page + 1)}
        leftField={
          <Select
            maxWidth="160px"
            size="tiny"
            dropdownPlacement="top-left"
            options={DELIVERY_AGENCY_ORDER_SORT_OPTIONS}
            value={DELIVERY_AGENCY_ORDER_SORT_OPTIONS.find((option) => option.value === sortBy)}
            onChange={(_: unknown, option: TypeSelectOption<TypeDeliveryAgencySortBy>) => onChangeSort(option.value)}
          />
        }
      />
    </div>
  );
};

export default DeliveryAgencyOrderTable;
